import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Search, Package, DollarSign, CheckCircle } from "lucide-react";
import { IProduct } from "@/services/mocks/productData";
import { productService } from "@/services";

interface ProductSelectorProps {
  onProductSelect: (product: IProduct | null) => void;
  selectedProduct: IProduct | null;
  disabled?: boolean;
  className?: string;
}

export const ProductSelector: React.FC<ProductSelectorProps> = ({
  onProductSelect,
  selectedProduct,
  disabled = false,
  className,
}) => {
  const [products, setProducts] = useState<IProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<IProduct[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar productos al montar el componente
  useEffect(() => {
    loadProducts();
  }, []);

  // Filtrar productos cuando cambie el término de búsqueda
  useEffect(() => {
    if (!searchTerm) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(
        (product) =>
          product.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.categoria.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const loadProducts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await productService.getAllProducts();
      // Filtrar solo productos que pueden ser canjeables (tickets/credits)
      const canjeableProducts = response.products.filter(
        (product) =>
          product.disponible &&
          (product.nombre.toLowerCase().includes("ticket") ||
            product.nombre.toLowerCase().includes("almuerzo") ||
            product.nombre.toLowerCase().includes("credito") ||
            product.nombre.toLowerCase().includes("comida") ||
            product.nombre.toLowerCase().includes("menú"))
      );
      setProducts(canjeableProducts);
      setFilteredProducts(canjeableProducts);
    } catch (error: any) {
      setError("Error al cargar los productos");
      console.error("Error cargando productos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductSelect = (product: IProduct | null) => {
    onProductSelect(product);
  };

  const handleClearSelection = () => {
    onProductSelect(null);
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className={className}>
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Producto seleccionado */}
          {selectedProduct && (
            <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900 dark:text-green-100">
                      {selectedProduct.nombre}
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      {selectedProduct.descripcion}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {selectedProduct.categoria}
                      </Badge>
                      <span className="text-sm font-medium text-green-700 dark:text-green-300">
                        {formatPrice(selectedProduct.precio)}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  disabled={disabled}
                  className="text-green-600 hover:text-green-700 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-950/40"
                >
                  Cambiar
                </Button>
              </div>
            </div>
          )}

          {/* Búsqueda de productos */}
          {!selectedProduct && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="product-search">Buscar Producto</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="product-search"
                    placeholder="Buscar por nombre, descripción o categoría..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={disabled || isLoading}
                    className="pl-10"
                  />
                  {isLoading && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>

              {/* Error */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Estado de carga */}
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <span className="ml-2 text-muted-foreground">
                    Cargando productos...
                  </span>
                </div>
              )}

              {/* Lista de productos */}
              {!isLoading && !error && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>
                        {searchTerm
                          ? "No se encontraron productos con ese término"
                          : "No hay productos disponibles"}
                      </p>
                    </div>
                  ) : (
                    filteredProducts.map((product) => (
                      <Button
                        key={product.id}
                        variant="outline"
                        className="w-full justify-start h-auto p-3 hover:bg-muted/50"
                        onClick={() => handleProductSelect(product)}
                        disabled={disabled}
                      >
                        <div className="flex items-center gap-3 text-left">
                          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {product.nombre}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {product.descripcion}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {product.categoria}
                              </Badge>
                              <div className="flex items-center gap-1 text-sm font-medium text-primary">
                                <DollarSign className="h-3 w-3" />
                                {formatPrice(product.precio)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Button>
                    ))
                  )}
                </div>
              )}

              {/* Información adicional */}
              {!isLoading && !error && filteredProducts.length > 0 && (
                <div className="text-xs text-muted-foreground text-center pt-2 border-t">
                  {filteredProducts.length} producto(s) disponible(s)
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 